import React from 'react';
import { useBreakpoint } from '@noths/polaris-client-responsive';
import {
  getProductImageURL,
  Hyperlink,
  IconCheckmark,
  Modal,
  ModalChildrenProps,
  OutlineButton,
  Placement,
  SolidButton,
} from '@noths/polaris-client-ribbons-design-system';
import { Breakpoint, visuallyHidden } from '@noths/polaris-client-styles';
import { priceFormatter } from '@noths/polaris-client-utils';

import { PriceBreakdownContainer } from 'src/components/molecules/PriceBreakdown';
import { RecommendedProductsShelfContainer } from 'src/components/organisms/RecommendedProductsShelf';
import { FavouriteProduct } from 'src/redux/selectors';
import { Basket } from 'src/types';
import {
  BASKET_MODAL_BUTTON_CONTINUE,
  BASKET_MODAL_BUTTON_CTA,
  BASKET_MODAL_CONTENT_LABEL,
  BASKET_MODAL_HEADER,
  BASKET_MODAL_ITEMS,
  BASKET_MODAL_QUANTITY,
  BASKET_MODAL_TOTAL,
} from '../constants';
import { useAddToCartPlacement } from '../hooks/useAddToCartPlacement';
import * as styles from '../styles/ProductInfo.styles';

export interface CartModalProps {
  product: FavouriteProduct;
  basket: Basket | null;
  onModalClose: VoidFunction;
  onModalApply: VoidFunction;
  isModalOpen: boolean;
  hideAddToCartPlacement: boolean;
  richRelevanceSessionId?: string;
}

const BasketInfoAndCTAs = ({
  basket,
  onModalApply,
  onModalClose,
}: Pick<CartModalProps, 'basket' | 'onModalApply' | 'onModalClose'>) => {
  return (
    <>
      <div css={styles.basketInfo}>
        {basket?.itemsTotal && (
          <p css={styles.basketInfoTotal}>
            {`${BASKET_MODAL_TOTAL}: ${priceFormatter(
              basket.itemsTotal.currency,
              basket.itemsTotal.cents,
            )}`}
          </p>
        )}
        {Boolean(basket?.itemsCount) && (
          <p css={styles.basketInfoItems}>
            {`${basket!.itemsCount} ${BASKET_MODAL_ITEMS}`}
            <span css={visuallyHidden}> in the basket</span>
          </p>
        )}
      </div>
      <div css={styles.buttonContainer}>
        <OutlineButton size="fullWidth" onClick={onModalClose}>
          {BASKET_MODAL_BUTTON_CONTINUE}
        </OutlineButton>
        <SolidButton
          href="/cart"
          onClick={(e) => {
            e.preventDefault();
            onModalApply();
          }}
          size="fullWidth"
          css={styles.goToBasketButton}
        >
          {BASKET_MODAL_BUTTON_CTA}
        </SolidButton>
      </div>
    </>
  );
};

export const CartModal = ({
  product: { title, partner, quantity, images, code },
  basket,
  onModalApply,
  onModalClose,
  isModalOpen,
  hideAddToCartPlacement,
  richRelevanceSessionId,
}: CartModalProps) => {
  const isOnSmallScreen = useBreakpoint() === Breakpoint.SMALL;

  useAddToCartPlacement(richRelevanceSessionId, code, isModalOpen);

  return (
    <>
      <Modal
        appRootSelector="#NOTHSPageWrapper"
        isModalOpen={isModalOpen}
        contentLabel={BASKET_MODAL_CONTENT_LABEL}
        onModalClose={onModalClose}
      >
        {({ Header, Body, Footer }: ModalChildrenProps) => (
          <>
            <Header onCloseButtonClick={onModalClose}>
              <div css={[styles.addToBasketSection, styles.addToBasketHeaderSection]}>
                <IconCheckmark width={24} height={24} />
                <h2 css={styles.addToBasketHeader}>{BASKET_MODAL_HEADER}</h2>
              </div>
            </Header>
            <Body>
              <div css={styles.bodyWrapper}>
                <div css={[styles.addToBasketSection, styles.addToBasketProductSection]}>
                  <div css={styles.partnerInfo}>
                    <img
                      css={styles.partnerImage}
                      alt=""
                      width={135}
                      height={135}
                      src={getProductImageURL(images[0].href, 'mini')}
                    />
                    <div>
                      <Hyperlink href={partner.links?.[1]?.rel || ''}>{partner.name}</Hyperlink>
                      <p css={styles.productDescription}>{title}</p>
                      <p css={styles.productQuantity}>{`${BASKET_MODAL_QUANTITY}: ${quantity}`}</p>
                    </div>
                  </div>
                  <div css={styles.basketBreakdownWrapper}>
                    <PriceBreakdownContainer showTotalDivider expandablePersonalisations />
                  </div>
                </div>
                <div css={[styles.addToBasketSection, styles.addToBasketButtonSection]}>
                  {!isOnSmallScreen && (
                    <BasketInfoAndCTAs
                      basket={basket}
                      onModalApply={onModalApply}
                      onModalClose={onModalClose}
                    />
                  )}
                </div>
                {!hideAddToCartPlacement && (
                  <div css={[styles.addToBasketSection, styles.addToBasketCarouselSection]}>
                    <RecommendedProductsShelfContainer
                      placementType={Placement.AddToCart}
                      size="small"
                    />
                  </div>
                )}
              </div>
            </Body>
            {isOnSmallScreen && (
              <Footer>
                <div css={styles.footerItems}>
                  <BasketInfoAndCTAs
                    basket={basket}
                    onModalApply={onModalApply}
                    onModalClose={onModalClose}
                  />
                </div>
              </Footer>
            )}
          </>
        )}
      </Modal>
      <styles.AddToBasketModalGlobalStyles />
    </>
  );
};
